import React, { createRef, useCallback, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Typography, Icon, alpha, Box, Menu, MenuItem, FormControlLabel, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { I18nKeys } from '../../constants/I18nKeys';
import { GridViewType } from '../../constants/GridViewType';
import { UserPreference } from '../../constants/User';
import { unknownGroup } from '../../constants/Group';
import selectGridIcon from '../../images/selectGridIcon.svg';
import { saveUserPreferences } from '../../ducks/currentUserSlice';
import { PageHeaderButton } from '../library/PageHeaderButton';
import { isCarportView } from '../../utils/clientIdUtils';
import { GridDataType, GridOptionCategory } from '../../constants/PricingBase';
import { PricingTab } from '../../constants/Pricing';

const useStyles = makeStyles(() => ({
  selectViewButton: {
    height: 'fit-content',
    borderRadius: '4px',
    justifyContent: 'flex-start',
    color: alpha('#000', 0.87),
    background: alpha('#000', 0.08),
    padding: '8px 18px',
  },
  selectViewIcon: {
    width: '24px',
    height: '24px',
  },
  selectViewText: {
    fontWeight: '500',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    lineHeight: '16px',
  },
  viewOptionText: {
    fontWeight: '400',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
    textTransform: 'capitalize',
  },
  viewOptionIcon: {
    width: '24px',
    height: '24px',
    paddingRight: '30px',
  },
  viewOptionButton: {
    border: 'border: 1px solid #F4F4F4',
    height: 'fit-content',
    color: '#424242',
    justifyContent: 'flex-start',
    background: '#FFFFFF',
    padding: '8px 48px',
  },
}));

type Props = {
  gridViewType: GridViewType;
  gridDataType?: GridDataType;
};

export const PricingSheetViewSelect: React.FC<Props> = ({ gridViewType, gridDataType = GridDataType.Price }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const viewerClientId = useAppSelector(
    (state) => state?.viewer?.selectedTabId || state?.viewer?.selectedClientId || '',
  );
  const { selectedPricingTabId: pricingTab } = useAppSelector((state) => state.viewer);
  const {
    group: { groupId } = unknownGroup,
    preferences: {
      [UserPreference.PricingBasePreferences]: pricingBasePreferences = { gridViewType: {}, gridDataType: {} },
    } = {},
  } = useAppSelector((state) => state.currentUser);

  const buttonElRef = createRef<HTMLButtonElement>();
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

  const showDataTypeOptions = !isCarportView(viewerClientId);

  const getOptionText = (viewType: GridViewType) =>
    t(viewType === GridViewType.Grid ? I18nKeys.PricingBaseViewTypeGrid : I18nKeys.PricingBaseViewTypeList);

  const getDataTypeText = (viewType: GridDataType) =>
    t(viewType === GridDataType.Price ? I18nKeys.PricingBaseDataTypePrice : I18nKeys.PricingBaseDataTypeSku);

  const handleSelectViewOption = useCallback(
    (option: string, optionType: GridOptionCategory) => {
      if (optionType === GridOptionCategory.View && gridViewType !== option) {
        const prefs = {
          ...pricingBasePreferences,
          gridViewType: {
            ...pricingBasePreferences.gridViewType,
            [groupId]: { ...(pricingBasePreferences.gridViewType[groupId] || {}), [viewerClientId]: option },
          },
        };
        dispatch(saveUserPreferences({ userPreference: UserPreference.PricingBasePreferences, preferences: prefs }));
      } else if (optionType === GridOptionCategory.Data && gridDataType !== option) {
        const prefs = {
          ...pricingBasePreferences,
          gridDataType: {
            ...pricingBasePreferences.gridDataType,
            [groupId]: { ...(pricingBasePreferences.gridDataType?.[groupId] || {}), [viewerClientId]: option },
          },
        };
        dispatch(saveUserPreferences({ userPreference: UserPreference.PricingBasePreferences, preferences: prefs }));
      }

      setFilterMenuAnchorEl(null);
    },
    [pricingBasePreferences, groupId, viewerClientId, gridViewType, gridDataType, dispatch],
  );

  if (pricingTab === PricingTab.SizeBased) return null;
  return (
    <Box>
      <PageHeaderButton
        ref={buttonElRef}
        id="header-change-view-type-button"
        icon={
          <Icon className={classes.selectViewIcon}>
            <img alt="change grid view type" src={selectGridIcon} />
          </Icon>
        }
        label="Change View"
        onClick={() => setFilterMenuAnchorEl(buttonElRef.current)}
      />
      <Menu
        id="header-change-view-type-menu"
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={() => setFilterMenuAnchorEl(null)}
      >
        {Object.values(GridViewType).map((option) => (
          <MenuItem
            disableGutters
            key={option}
            sx={{ py: '0px', pl: '7px', pr: '16px' }}
            onClick={() => handleSelectViewOption(option, GridOptionCategory.View)}
          >
            <FormControlLabel
              sx={{ mx: '0px' }}
              control={
                <Check
                  sx={{
                    m: '9px',
                    color: (theme) => (gridViewType === option ? theme.palette.secondary.main : 'transparent'),
                  }}
                />
              }
              label={<Typography>{getOptionText(option)}</Typography>}
            />
          </MenuItem>
        ))}
        {showDataTypeOptions && (
          <>
            <Divider />
            {Object.values(GridDataType).map((option) => (
              <MenuItem
                disableGutters
                key={option}
                sx={{ py: '0px', pl: '7px', pr: '16px' }}
                onClick={() => handleSelectViewOption(option, GridOptionCategory.Data)}
              >
                <FormControlLabel
                  sx={{ mx: '0px' }}
                  control={
                    <Check
                      sx={{
                        m: '9px',
                        color: (theme) => (gridDataType === option ? theme.palette.secondary.main : 'transparent'),
                      }}
                    />
                  }
                  label={<Typography>{getDataTypeText(option)}</Typography>}
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </Box>
  );
};
